import type { HTMLProps } from 'react';

import { Typography } from '@remarkable/ark-web';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import {
  type ImageSource,
  ImageWithFallback,
  Sheet,
  type VideoSource,
  VideoWithFallback,
} from 'src/components';

type NewsCardMedia =
  | { imageSources: ImageSource[]; videoSources?: never; poster?: never }
  | { imageSources?: never; videoSources: VideoSource[]; poster: string };

type NewsCardProps = HTMLProps<HTMLDivElement> &
  NewsCardMedia & {
    mediaAlt: string;
    mediaLink: string;
    headline?: string;
    mediaClassname?: string;
    mediaOnClick?: () => void;
    openNewTab?: boolean;
  };

export const NewsCard = ({
  mediaAlt,
  mediaLink,
  imageSources,
  videoSources,
  poster,
  children,
  mediaClassname,
  mediaOnClick,
  headline = 'News',
  openNewTab = false,
  ...props
}: NewsCardProps) => {
  return (
    <Sheet {...props} className={twMerge('!p-0 lg:flex-row', props.className)}>
      <div className="max-h-[350px] w-full lg:max-h-full lg:w-[45%]">
        <a
          href={mediaLink}
          target={openNewTab ? '_blank' : '_self'}
          rel={openNewTab ? 'noopener noreferrer' : undefined}
          onClick={mediaOnClick}
        >
          {imageSources ? (
            <ImageWithFallback
              imgClassName={cx(
                'w-full h-full max-h-[350px] min-h-[220px] lg:max-h-full object-cover',
                mediaClassname
              )}
              sources={imageSources}
              alt={mediaAlt}
            />
          ) : (
            <VideoWithFallback
              className={cx(
                'w-full h-full max-h-[350px] min-h-[220px] lg:max-h-full object-cover',
                mediaClassname
              )}
              sources={videoSources}
              poster={poster}
              alt={mediaAlt}
            />
          )}
        </a>
      </div>
      <div className="flex w-full flex-col justify-center gap-16 p-24 text-left lg:w-1/2 lg:flex-1 lg:p-64">
        <Typography
          as="span"
          className="m-0 text-text-muted"
          variant="interface-sm"
          fontWeight="medium"
          leadingNone
          uppercase
        >
          {headline}
        </Typography>
        {children}
      </div>
    </Sheet>
  );
};
